import { getResourceEntity } from 'js/lib/PipelineUtils';
import useEntities from 'new/state/entities/repository/useEntities';
import getFlowItems from 'js/components/pipelines/ExpandedPipelines/utils/getFlowItems';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

const getResource = (flow: any, entities: any) => {
  return getResourceEntity(flow, entities, true, ['name', 'description']);
};

export default function useTriggeringFlows(flows: any[]) {
  const branches = useSelector((state: any) => state.pipelines.branches);
  const { data: entities } = useEntities();
  /**
   * ISSUE: Linked Flows Not Updating When Fetched
   *
   * Problem: When linked flows were fetched asynchronously, the linksMap didn't recalculate
   * because the hook wasn't detecting the data change. This happened because:
   * - The hook was recalculating on every render (not memoized)
   * - But it wasn't tracking when pipeline data actually changed
   * - So linked flows fetched asynchronously weren't included in linksMap
   *
   * Solution: Track lastUpdated timestamp and branchesKeys to detect when pipeline data
   * changes. This ensures linksMap recalculates when linked flows are fetched.
   */
  const lastUpdated = useSelector((state: any) => state.pipelines?.lastUpdated);
  // Also track branches to detect when branches update (needed for getFlowItems)
  const branchesKeys = useSelector((state: any) =>
    state.pipelines?.branches ? Object.keys(state.pipelines.branches).join(',') : '',
  );

  /**
   * ISSUE: Linked Flows Not Updating When Fetched (continued)
   *
   * Problem: The hook was recalculating on every render, which was inefficient and
   * could cause stale data issues.
   *
   * Solution: Wrap the calculation in useMemo with proper dependencies. This ensures:
   * - The calculation only runs when dependencies change
   * - linksMap is recalculated when linked flows are fetched (via lastUpdated)
   * - The hook is more performant by avoiding unnecessary recalculations
   */
  return useMemo(() => {
    if (!flows?.length) {
      return {};
    }

    const { flowNodes: flowNodesData, projects: projectMap } = entities;

    const { triggerFlows, triggeredFlows, linkedFlows } = flows.reduce(
      (obj, flow) => {
        if (flow.flow_triggers?.length) {
          obj.triggeredFlows.push(flow.flow_node_id);
          flow.flow_triggers.forEach((flow) => {
            obj.triggerFlows.push(flow.triggering_origin_node_id);
          });
        }
        if (flow.linked_flows?.length && !obj.linkedFlows.includes(flow.flow_node_id)) {
          flow.linked_flows.forEach((f) => {
            obj.linkedFlows.push(f);
          });
        }
        return obj;
      },
      { triggerFlows: [], triggeredFlows: [], linkedFlows: [] },
    );

    const triggeringMap = flows.reduce((map, flow) => {
      if (triggeredFlows.includes(flow.flow_node_id)) {
        return { ...map, ...mapTriggeringFlows(flow) };
      }
      return map;
    }, {});

    /**
     * Build linksMap by iterating through all flows and their linked_flows.
     *
     * ISSUE: Linked Flows Not Updating When Fetched (linksMap calculation)
     *
     * Problem: The linksMap wasn't being recalculated when linked flows were fetched,
     * so the flow canvas didn't show the newly fetched linked flows.
     *
     * Solution: Always create a new object for each linked flow to ensure reference
     * changes are detected by React's dependency system. This ensures that when
     * linked flows are fetched and entities are updated, the linksMap will have
     * new references and trigger re-renders.
     */
    const buildLinksMap = () => {
      const result: Record<string, any> = {};
      flows.forEach((flow) => {
        if (flow.linked_flows?.length) {
          flow.linked_flows.forEach((linkedFlowId) => {
            const linkedFlow = getFlow(linkedFlowId);
            if (linkedFlow) {
              // Always create a new object to ensure reference changes are detected
              result[linkedFlowId] = linkedFlow;
            }
          });
        }
      });
      return result;
    };

    const linksMap = buildLinksMap();

    function getFlow(flowId) {
      const flowEntity = getResourceEntity(flowNodesData[flowId], entities, true, [
        'name',
        'description',
      ]);
      if (!flowEntity) return undefined;
      const items = getFlowItems(flowEntity, { entities, branches });
      return {
        ...flowEntity,
        project: projectMap[flowEntity.project_id],
        resource_type: 'flow',
        items,
      };
    }

    function mapFlowLinks(flow, useMap = true, processedLinksMap: Record<string, boolean>) {
      return flow.linked_flows.reduce((map, linkedFlowId) => {
        const key = `${Math.min(flow.id, linkedFlowId)}-${Math.max(flow.id, linkedFlowId)}`;
        if (processedLinksMap[key]) {
          return map;
        }
        const flowEntity = getResourceEntity(flowNodesData[linkedFlowId], entities, true, [
          'name',
          'description',
        ]);
        if (!flowEntity) return map;
        // Only mark as processed after successfully getting the flow entity
        processedLinksMap[key] = true;
        const items = getFlowItems(flowEntity, { entities, branches });
        map[flowEntity.id] = {
          ...flowEntity,
          project: projectMap[flowEntity.project_id],
          resource_type: 'flow',
          items,
        };
        return map;
      }, {});
    }

    function mapTriggeringFlows(flow: any, useMap = true) {
      return flow.flow_triggers.reduce((triggerMap, triggerFlow) => {
        const triggeringFlow = flowNodesData[triggerFlow.triggering_origin_node_id];
        /**
         * resource merged over flowNode
         */
        const triggerResource = getResource(triggeringFlow, entities);

        if (!triggerResource) return triggerMap;

        const items = getFlowItems(triggerResource, { entities, branches });
        const returnedTriggerFlow = Object.assign(triggerFlow, triggerResource);

        triggerMap[triggerResource.flow_node_id] = {
          ...returnedTriggerFlow,
          project: projectMap[triggerResource.project_id],
          renderProjectLabel: true,
          resource_type: 'flow',
          items,
        };

        return triggerMap;
      }, {});
    }

    return {
      triggeredFlows,
      triggeringMap,
      triggerFlows,
      linksMap,
      linkedFlows,
      mapFlowLinks,
      mapTriggeringFlows,
    };
  }, [flows, entities, branches, lastUpdated, branchesKeys]); // Use lastUpdated and branchesKeys to detect changes
}
